// Stock y Lotes Page - Vista detallada con trazabilidad APPCC
import React, { useEffect, useState } from 'react';
import { stockAPI, ingredientesAPI, getAuthToken } from '../api/api';
import { useAuth } from '../context/AuthContext';
import DiagnosticoLibroStock from '../components/DiagnosticoLibroStock';
import { 
  Package, Search, Plus, Minus, AlertTriangle, History, X, 
  Calendar, Box, TrendingUp, TrendingDown, Filter, ChevronDown, ChevronRight,
  RefreshCw, Archive, Clock, CheckCircle, AlertCircle, Bell, MapPin,
  Edit2, Trash2
} from 'lucide-react';

const StockLotes = () => {
  const { user } = useAuth();
  // Diagnóstico del libro de stock (lote 5b): solo dirección
  const esDireccion = !!user && (user.rol === 'direccion' || user.role === 'direccion');
  const [showDiagnosticoLibro, setShowDiagnosticoLibro] = useState(false);
  const [stockConLotes, setStockConLotes] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('stock'); // 'stock', 'caducidades', 'movimientos'
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('todos'); // todos, bajo, agotado, ok
  const [enviandoAlerta, setEnviandoAlerta] = useState(false);
  const [editingMovimiento, setEditingMovimiento] = useState(null); // Para editar movimiento
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLote, setEditingLote] = useState(null); // Para editar lote
  const [showEditLoteModal, setShowEditLoteModal] = useState(false);
  
  const [movementForm, setMovementForm] = useState({
    tipo_movimiento: 'entrada',
    cantidad: '',
    motivo: '',
    ubicacion: 'almacen',
    lote: '',
    fecha_caducidad: '',
    numero_pedido: ''
  });

  const [entradaForm, setEntradaForm] = useState({
    ingrediente_id: '',
    cantidad: '',
    lote: '',
    fecha_caducidad: '',
    ubicacion: 'almacen',
    motivo: 'compra',
    numero_pedido: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      // Fetch stock con lotes (nuevo endpoint APPCC)
      const stockConLotesRes = await fetch(`${API_URL}/api/stock/con-lotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (stockConLotesRes.ok) {
        const stockData = await stockConLotesRes.json();
        setStockConLotes(stockData || []);
      }
      
      // Fetch ingredientes y proveedores
      const [ingredientesRes, proveedoresRes] = await Promise.all([
        ingredientesAPI.getAll(),
        fetch(`${API_URL}/api/proveedores`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setIngredientes(ingredientesRes.data || []);
      
      if (proveedoresRes.ok) {
        const provData = await proveedoresRes.json();
        setProveedores(provData || []);
      }
      
      // Fetch movimientos recientes
      const movimientosRes = await fetch(`${API_URL}/api/stock/movimientos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (movimientosRes.ok) {
        const movData = await movimientosRes.json();
        setMovimientos(movData || []);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (stockItem) => {
    const cantidad = stockItem.stock_calculado_lotes || stockItem.cantidad_actual || 0;
    if (cantidad === 0) {
      return { color: 'bg-red-100 text-red-800 border-red-200', label: 'Agotado', icon: <AlertTriangle className="w-4 h-4" /> };
    }
    if (stockItem.stock_minimo && cantidad <= stockItem.stock_minimo) {
      return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Stock bajo', icon: <AlertCircle className="w-4 h-4" /> };
    }
    return { color: 'bg-green-100 text-green-800 border-green-200', label: 'OK', icon: <CheckCircle className="w-4 h-4" /> };
  };

  const getCaducidadStatus = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date();
    const fechaCad = new Date(fecha);
    const diasRestantes = Math.ceil((fechaCad - hoy) / (1000 * 60 * 60 * 24));
    
    if (diasRestantes < 0) {
      return { color: 'bg-red-600 text-white', label: 'CADUCADO', dias: diasRestantes };
    }
    if (diasRestantes <= 2) {
      return { color: 'bg-red-500 text-white', label: `${diasRestantes}d`, dias: diasRestantes };
    }
    if (diasRestantes <= 7) {
      return { color: 'bg-red-100 text-red-800', label: `${diasRestantes}d`, dias: diasRestantes };
    }
    if (diasRestantes <= 30) {
      return { color: 'bg-yellow-100 text-yellow-800', label: `${diasRestantes}d`, dias: diasRestantes };
    }
    return { color: 'bg-green-100 text-green-800', label: `${diasRestantes}d`, dias: diasRestantes };
  };

  const filteredStock = stockConLotes.filter(item => {
    const matchesSearch = item.ingrediente_nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filtroEstado === 'todos') return true;
    
    const status = getStockStatus(item);
    if (filtroEstado === 'agotado') return status.label === 'Agotado';
    if (filtroEstado === 'bajo') return status.label === 'Stock bajo';
    if (filtroEstado === 'ok') return status.label === 'OK';
    
    return true;
  });

  // Calcular estadísticas
  const stats = {
    total: stockConLotes.length,
    agotado: stockConLotes.filter(s => (s.stock_calculado_lotes || s.cantidad_actual || 0) === 0).length,
    bajo: stockConLotes.filter(s => {
      const cantidad = s.stock_calculado_lotes || s.cantidad_actual || 0;
      return cantidad > 0 && s.stock_minimo && cantidad <= s.stock_minimo;
    }).length,
    ok: stockConLotes.filter(s => {
      const cantidad = s.stock_calculado_lotes || s.cantidad_actual || 0;
      return cantidad > 0 && (!s.stock_minimo || cantidad > s.stock_minimo);
    }).length,
    lotesProximosCaducar: stockConLotes.reduce((count, item) => {
      return count + (item.lotes || []).filter(l => {
        const status = getCaducidadStatus(l.fecha_caducidad);
        return status && status.dias <= 7 && status.dias >= 0;
      }).length;
    }, 0),
    totalLotes: stockConLotes.reduce((count, item) => count + (item.total_lotes || 0), 0)
  };

  const validateEntradaForm = () => {
    const errors = {};
    
    if (!entradaForm.ingrediente_id) {
      errors.ingrediente_id = 'Selecciona un ingrediente';
    }
    if (!entradaForm.cantidad || parseFloat(entradaForm.cantidad) <= 0) {
      errors.cantidad = 'La cantidad debe ser mayor a 0';
    }
    if (!entradaForm.lote || entradaForm.lote.trim() === '') {
      errors.lote = 'El nº de lote es obligatorio (APPCC)';
    }
    if (!entradaForm.fecha_caducidad) {
      errors.fecha_caducidad = 'La fecha de caducidad es obligatoria (APPCC)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEntradaMercancia = async () => {
    if (!validateEntradaForm()) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/movimiento`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ingrediente_id: entradaForm.ingrediente_id,
          tipo_movimiento: 'entrada',
          cantidad: parseFloat(entradaForm.cantidad),
          motivo: entradaForm.motivo || 'compra',
          ubicacion: entradaForm.ubicacion || 'almacen',
          lote: entradaForm.lote,
          fecha_caducidad: entradaForm.fecha_caducidad
        })
      });
      
      if (response.ok) {
        setShowEntradaModal(false);
        setEntradaForm({
          ingrediente_id: '',
          cantidad: '',
          lote: '',
          fecha_caducidad: '',
          ubicacion: 'almacen',
          motivo: 'compra'
        });
        setFormErrors({});
        fetchData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'No se pudo registrar la entrada'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al registrar entrada de mercancía');
    }
  };

  const handleMovimiento = async () => {
    if (!selectedStock || !movementForm.cantidad) return;
    
    // Validar campos obligatorios para entradas
    if (movementForm.tipo_movimiento === 'entrada') {
      if (!movementForm.lote) {
        alert('El número de lote es obligatorio para entradas (APPCC)');
        return;
      }
      if (!movementForm.fecha_caducidad) {
        alert('La fecha de caducidad es obligatoria para entradas (APPCC)');
        return;
      }
    }
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/movimiento`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ingrediente_id: selectedStock.ingrediente_id,
          tipo_movimiento: movementForm.tipo_movimiento,
          cantidad: parseFloat(movementForm.cantidad),
          motivo: movementForm.motivo,
          ubicacion: movementForm.ubicacion || 'almacen',
          lote: movementForm.lote || null,
          fecha_caducidad: movementForm.fecha_caducidad || null,
          numero_pedido: movementForm.numero_pedido || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al registrar movimiento');
      }
      
      setShowMovementModal(false);
      setMovementForm({ tipo_movimiento: 'entrada', cantidad: '', motivo: '', ubicacion: 'almacen', lote: '', fecha_caducidad: '', numero_pedido: '' });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al registrar movimiento');
    }
  };

  const enviarAlertaCaducidad = async () => {
    setEnviandoAlerta(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/alertas-caducidad/ejecutar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('✅ Alerta de caducidades enviada por Telegram');
      } else {
        alert('⚠️ No se pudo enviar la alerta. Verifica la configuración de Telegram.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar alerta');
    } finally {
      setEnviandoAlerta(false);
    }
  };

  // Funciones para editar y eliminar movimientos
  const [lotesDisponibles, setLotesDisponibles] = useState([]);
  
  const handleEditMovimiento = async (movimiento) => {
    // Obtener los lotes disponibles para este ingrediente
    const ingredienteId = movimiento.ingrediente_id;
    const stockItem = stockData.find(s => s.id === ingredienteId || s.ingrediente_id === ingredienteId);
    
    if (stockItem && stockItem.lotes) {
      setLotesDisponibles(stockItem.lotes.filter(l => l.cantidad_actual > 0 || l.id === movimiento.lote_id));
    } else {
      setLotesDisponibles([]);
    }
    
    setEditingMovimiento({
      ...movimiento,
      cantidad: movimiento.cantidad?.toString() || '',
      motivo: movimiento.motivo || '',
      lote: movimiento.lote || '',
      lote_id: movimiento.lote_id || '',
      numero_pedido: movimiento.numero_pedido || ''
    });
    setShowEditModal(true);
  };

  const handleSaveMovimiento = async () => {
    if (!editingMovimiento) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/movimientos/${editingMovimiento.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cantidad: parseFloat(editingMovimiento.cantidad) || undefined,
          motivo: editingMovimiento.motivo || undefined,
          lote: editingMovimiento.lote || undefined,
          lote_id: editingMovimiento.lote_id || undefined,
          numero_pedido: editingMovimiento.numero_pedido || undefined
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.detail || 'Error al actualizar movimiento');
      }
      
      setShowEditModal(false);
      setEditingMovimiento(null);
      fetchData();
      alert('✅ Movimiento actualizado correctamente');
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al actualizar movimiento');
    }
  };

  const handleDeleteMovimiento = async (movimientoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este movimiento? Se revertirá el efecto en el stock.')) {
      return;
    }
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/movimientos/${movimientoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al eliminar movimiento');
      }
      
      fetchData();
      alert('✅ Movimiento eliminado y stock actualizado');
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al eliminar movimiento');
    }
  };

  // Funciones para editar/borrar lotes
  const handleEditLote = (lote, ingredienteId) => {
    setEditingLote({
      ...lote,
      ingrediente_id: ingredienteId,
      cantidad_actual: lote.cantidad_actual?.toString() || '',
      fecha_caducidad: lote.fecha_caducidad ? lote.fecha_caducidad.split('T')[0] : '',
      ubicacion: lote.ubicacion || 'almacen'
    });
    setShowEditLoteModal(true);
  };

  const handleSaveLote = async () => {
    if (!editingLote) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/lotes/${editingLote.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cantidad_actual: parseFloat(editingLote.cantidad_actual) || 0,
          fecha_caducidad: editingLote.fecha_caducidad || null,
          ubicacion: editingLote.ubicacion || 'almacen',
          lote_proveedor: editingLote.lote_proveedor || null
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.detail || 'Error al actualizar lote');
      }
      
      setShowEditLoteModal(false);
      setEditingLote(null);
      fetchData();
      alert('✅ Lote actualizado correctamente');
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al actualizar lote');
    }
  };

  const handleDeleteLote = async (loteId, ingredienteId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lote? Se eliminará todo el stock asociado.')) {
      return;
    }
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/stock/lotes/${loteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al eliminar lote');
      }
      
      fetchData();
      alert('✅ Lote eliminado correctamente');
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al eliminar lote');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Stock y Lotes APPCC</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de inventario con trazabilidad completa por lotes</p>
        </div>
        <div className="flex gap-2">
          {esDireccion && (
            <button
              onClick={() => setShowDiagnosticoLibro(true)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              title="Informe en seco de db.stock frente a los lotes y las reservas"
              data-testid="diagnostico-libro-btn"
            >
              <History className="w-5 h-5" />
              <span className="hidden sm:inline">Diagnóstico del libro</span>
            </button>
          )}
          <button
            onClick={enviarAlertaCaducidad}
            disabled={enviandoAlerta}
            className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition disabled:opacity-50"
            title="Enviar alerta de caducidades por Telegram"
          >
            <Bell className="w-5 h-5" />
            <span className="hidden sm:inline">Alertar Caducidades</span>
          </button>
          <button
            onClick={() => setShowEntradaModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            <span>Entrada de Mercancía</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-gray-600 text-xs mb-1">
            <Package className="w-4 h-4" />
            <span>Ingredientes</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
          <div className="flex items-center gap-2 text-blue-600 text-xs mb-1">
            <Box className="w-4 h-4" />
            <span>Total Lotes</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">{stats.totalLotes}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
          <div className="flex items-center gap-2 text-green-600 text-xs mb-1">
            <CheckCircle className="w-4 h-4" />
            <span>Stock OK</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.ok}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-yellow-200 shadow-sm">
          <div className="flex items-center gap-2 text-yellow-600 text-xs mb-1">
            <AlertCircle className="w-4 h-4" />
            <span>Stock Bajo</span>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{stats.bajo}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-red-200 shadow-sm">
          <div className="flex items-center gap-2 text-red-600 text-xs mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span>Agotado</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.agotado}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm">
          <div className="flex items-center gap-2 text-orange-600 text-xs mb-1">
            <Clock className="w-4 h-4" />
            <span>Próx. Caducar</span>
          </div>
          <p className="text-2xl font-bold text-orange-700">{stats.lotesProximosCaducar}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {[
          { id: 'stock', label: 'Stock por Lotes', icon: Package },
          { id: 'caducidades', label: 'Caducidades', icon: Calendar },
          { id: 'movimientos', label: 'Movimientos', icon: History }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar ingrediente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="todos">Todos</option>
            <option value="ok">Stock OK</option>
            <option value="bajo">Stock Bajo</option>
            <option value="agotado">Agotado</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Actualizar"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Stock Tab Content - Con lotes expandibles */}
      {activeTab === 'stock' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ingrediente</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Stock Total</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Reservado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Disponible</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Lotes</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Próx. Caducidad</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStock.map((stockItem) => {
                  const status = getStockStatus(stockItem);
                  const isExpanded = expandedItems[stockItem.id];
                  const caducidadStatus = getCaducidadStatus(stockItem.proxima_caducidad);
                  const stockReal = stockItem.stock_calculado_lotes || stockItem.cantidad_actual || 0;
                  const stockReservado = stockItem.stock_reservado || 0;
                  const stockDisponible = stockItem.stock_disponible ?? (stockReal - stockReservado);
                  
                  return (
                    <React.Fragment key={stockItem.id}>
                      <tr className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleExpand(stockItem.id)}
                            className="flex items-center gap-2 text-left w-full"
                            data-testid={`expand-btn-${stockItem.id}`}
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-blue-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <div>
                              <p className="font-medium text-gray-900">{stockItem.ingrediente_nombre}</p>
                            </div>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-gray-900">
                            {stockReal.toFixed(2)} {stockItem.unidad}
                          </span>
                          {stockItem.stock_minimo > 0 && (
                            <p className="text-xs text-gray-400">Mín: {stockItem.stock_minimo}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {stockReservado > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title="Reservado para eventos">
                              <Clock className="w-3 h-3" />
                              {stockReservado.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${stockDisponible <= 0 ? 'text-red-600' : stockDisponible < (stockItem.stock_minimo || 0) ? 'text-amber-600' : 'text-green-600'}`}>
                            {stockDisponible.toFixed(2)} {stockItem.unidad}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            stockItem.total_lotes > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <Box className="w-3 h-3" />
                            {stockItem.total_lotes || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {caducidadStatus ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${caducidadStatus.color}`}>
                              <Calendar className="w-3 h-3" />
                              {caducidadStatus.label}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            {status.icon}
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => {
                                setSelectedStock(stockItem);
                                setMovementForm({ ...movementForm, tipo_movimiento: 'entrada' });
                                setShowMovementModal(true);
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Entrada"
                              data-testid={`entrada-btn-${stockItem.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedStock(stockItem);
                                setMovementForm({ ...movementForm, tipo_movimiento: 'salida' });
                                setShowMovementModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Salida"
                              data-testid={`salida-btn-${stockItem.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Fila expandida con lotes */}
                      {isExpanded && (
                        <tr className="bg-blue-50/30">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="ml-6 p-4 bg-white rounded-lg border border-blue-200">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                  <Box className="w-4 h-4 text-blue-600" />
                                  Lotes activos ({stockItem.total_lotes || 0})
                                </p>
                                <p className="text-xs text-gray-500">
                                  Ordenados por fecha de caducidad (FEFO)
                                </p>
                              </div>
                              
                              {stockItem.lotes && stockItem.lotes.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 px-2 font-medium text-gray-600">Nº Lote</th>
                                        <th className="text-right py-2 px-2 font-medium text-gray-600">Cantidad</th>
                                        <th className="text-right py-2 px-2 font-medium text-gray-600">Reservado</th>
                                        <th className="text-center py-2 px-2 font-medium text-gray-600">Caducidad</th>
                                        <th className="text-left py-2 px-2 font-medium text-gray-600">Ubicación</th>
                                        <th className="text-left py-2 px-2 font-medium text-gray-600">Proveedor</th>
                                        <th className="text-left py-2 px-2 font-medium text-gray-600">Recepción</th>
                                        <th className="text-center py-2 px-2 font-medium text-gray-600">Acciones</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {stockItem.lotes.map((lote, idx) => {
                                        const loteCaducidad = getCaducidadStatus(lote.fecha_caducidad);
                                        // Solo las reservas vivas (lote 5b): las de eventos celebrados o cancelados no bloquean
                                        const reservasLote = (lote.reservas || []).filter(r => r.estado === 'reservado' && r.viva !== false);
                                        const cantidadReservada = reservasLote.reduce((sum, r) => sum + (r.cantidad_reservada || 0), 0);
                                        
                                        return (
                                          <tr key={lote.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-2 px-2 font-mono text-xs">{lote.lote_proveedor || '-'}</td>
                                            <td className="py-2 px-2 text-right font-medium">
                                              {(lote.cantidad_actual || 0).toFixed(2)} {lote.unidad}
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                              {cantidadReservada > 0 ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title={reservasLote.map(r => `Evento ${r.fecha_evento}: ${r.cantidad_reservada}`).join(', ')}>
                                                  <Clock className="w-3 h-3" />
                                                  {cantidadReservada.toFixed(2)}
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                              {loteCaducidad ? (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${loteCaducidad.color}`}>
                                                  {lote.fecha_caducidad ? new Date(lote.fecha_caducidad).toLocaleDateString('es-ES') : '-'}
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="py-2 px-2 text-gray-600">{lote.ubicacion || 'almacen'}</td>
                                            <td className="py-2 px-2 text-gray-600 truncate max-w-[120px]" title={lote.proveedor_nombre}>
                                              {lote.proveedor_nombre || '-'}
                                            </td>
                                            <td className="py-2 px-2 text-gray-500 text-xs">
                                              {lote.fecha_recepcion ? new Date(lote.fecha_recepcion).toLocaleDateString('es-ES') : '-'}
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                              <div className="flex items-center justify-center gap-1">
                                                <button
                                                  onClick={() => handleEditLote(lote, stockItem.id)}
                                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                  title="Editar lote"
                                                >
                                                  <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteLote(lote.id, stockItem.id)}
                                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                  title="Eliminar lote"
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <Archive className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                                  <p className="text-sm text-gray-400">Sin lotes registrados</p>
                                  <p className="text-xs text-gray-400 mt-1">Registra una entrada con nº de lote para comenzar la trazabilidad</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredStock.length === 0 && (
            <div className="p-8 text-center">
              <Archive className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No se encontraron ingredientes</p>
            </div>
          )}
        </div>
      )}

      {/* Caducidades Tab Content */}
      {activeTab === 'caducidades' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
            <h3 className="font-semibold text-gray-900">Control de Caducidades APPCC</h3>
            <p className="text-sm text-gray-600">Lotes ordenados por fecha de caducidad - FEFO (First Expired First Out)</p>
          </div>
          <div className="divide-y divide-gray-200">
            {stockConLotes
              .flatMap(item => (item.lotes || []).map(lote => ({
                ...lote,
                ingrediente_nombre: item.ingrediente_nombre
              })))
              .filter(lote => lote.fecha_caducidad)
              .sort((a, b) => new Date(a.fecha_caducidad) - new Date(b.fecha_caducidad))
              .slice(0, 50)
              .map((lote, idx) => {
                const caducidadStatus = getCaducidadStatus(lote.fecha_caducidad);
                
                return (
                  <div key={lote.id || idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${caducidadStatus?.color || 'bg-gray-100'}`}>
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{lote.ingrediente_nombre}</p>
                        <p className="text-sm text-gray-500">
                          Lote: <span className="font-mono">{lote.lote_proveedor || 'N/A'}</span>
                        </p>
                        <p className="text-xs text-gray-400">{lote.ubicacion} | {(lote.cantidad_actual || 0).toFixed(2)} {lote.unidad}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{new Date(lote.fecha_caducidad).toLocaleDateString('es-ES')}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${caducidadStatus?.color || ''}`}>
                        {caducidadStatus?.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            {stockConLotes.flatMap(item => item.lotes || []).filter(l => l.fecha_caducidad).length === 0 && (
              <div className="p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No hay lotes con fecha de caducidad registrada</p>
                <p className="text-sm text-gray-400 mt-2">Registra entradas con fecha de caducidad para habilitar el control APPCC</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Movimientos Tab Content */}
      {activeTab === 'movimientos' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
            <h3 className="font-semibold text-gray-900">Historial de Movimientos</h3>
            <p className="text-sm text-gray-600">Últimos 100 movimientos de stock • Click en un movimiento para editar</p>
          </div>
          
          {/* Tabla de movimientos */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Ingrediente</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Motivo</th>
                  <th className="px-4 py-3 text-left">Nº Pedido</th>
                  <th className="px-4 py-3 text-left">Lote</th>
                  <th className="px-4 py-3 text-right">Cantidad</th>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {movimientos.slice(0, 100).map((mov, idx) => {
                  const ingrediente = ingredientes.find(i => i.id === mov.ingrediente_id);
                  const esEntrada = mov.tipo_movimiento === 'entrada';
                  const esSalida = mov.tipo_movimiento === 'salida' || mov.tipo_movimiento === 'merma';
                  
                  return (
                    <tr key={mov.id || idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            esEntrada ? 'bg-green-100 text-green-600' : 
                            esSalida ? 'bg-red-100 text-red-600' : 
                            'bg-yellow-100 text-yellow-600'
                          }`}>
                            {esEntrada ? <TrendingUp className="w-4 h-4" /> : 
                             esSalida ? <TrendingDown className="w-4 h-4" /> : 
                             <RefreshCw className="w-4 h-4" />}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{ingrediente?.nombre || 'Desconocido'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          esEntrada ? 'bg-green-100 text-green-700' : 
                          esSalida ? 'bg-red-100 text-red-700' : 
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {mov.tipo_movimiento}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{mov.motivo}</td>
                      <td className="px-4 py-3">
                        {mov.numero_pedido ? (
                          <span className="inline-flex px-2 py-1 text-xs font-mono bg-blue-50 text-blue-700 rounded">
                            {mov.numero_pedido}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {mov.lote ? (
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{mov.lote}</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${esEntrada ? 'text-green-600' : esSalida ? 'text-red-600' : 'text-gray-600'}`}>
                          {esEntrada ? '+' : esSalida ? '-' : ''}{mov.cantidad?.toFixed(2)} {mov.unidad}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {mov.created_at ? new Date(mov.created_at).toLocaleString('es-ES', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        }) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEditMovimiento(mov)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Editar movimiento"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteMovimiento(mov.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Eliminar movimiento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {movimientos.length === 0 && (
            <div className="p-8 text-center">
              <History className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No hay movimientos registrados</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Movimiento Rápido */}
      {showMovementModal && selectedStock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className={`p-4 rounded-t-xl ${movementForm.tipo_movimiento === 'entrada' ? 'bg-green-600' : 'bg-red-600'} text-white`}>
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg">
                  {movementForm.tipo_movimiento === 'entrada' ? '📥 Entrada de Stock' : '📤 Salida de Stock'}
                </h3>
                <button onClick={() => setShowMovementModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-white/80 mt-1">
                {selectedStock.ingrediente_nombre}
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-sm text-gray-600">Stock actual</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(selectedStock.stock_calculado_lotes || selectedStock.cantidad_actual || 0).toFixed(2)} {selectedStock.unidad}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad *</label>
                <input
                  type="number"
                  value={movementForm.cantidad}
                  onChange={(e) => setMovementForm({ ...movementForm, cantidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  step="0.01"
                  min="0"
                  data-testid="movimiento-cantidad"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
                <select
                  value={movementForm.motivo}
                  onChange={(e) => setMovementForm({ ...movementForm, motivo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="movimiento-motivo"
                >
                  <option value="">Seleccionar motivo...</option>
                  {movementForm.tipo_movimiento === 'entrada' ? (
                    <>
                      <option value="compra">Compra</option>
                      <option value="devolucion">Devolución</option>
                      <option value="ajuste_inventario">Ajuste de inventario</option>
                      <option value="otro">Otro</option>
                    </>
                  ) : (
                    <>
                      <option value="consumo">Consumo</option>
                      <option value="uso_evento">Uso en evento</option>
                      <option value="merma">Merma</option>
                      <option value="caducidad">Caducidad</option>
                      <option value="ajuste_inventario">Ajuste de inventario</option>
                      <option value="otro">Otro</option>
                    </>
                  )}
                </select>
              </div>
              
              {movementForm.tipo_movimiento === 'entrada' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº Lote Proveedor *
                      <span className="text-red-500 ml-1">(Obligatorio APPCC)</span>
                    </label>
                    <input
                      type="text"
                      value={movementForm.lote}
                      onChange={(e) => setMovementForm({ ...movementForm, lote: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Número de lote del proveedor"
                      data-testid="movimiento-lote"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Caducidad *
                      <span className="text-red-500 ml-1">(Obligatorio APPCC)</span>
                    </label>
                    <input
                      type="date"
                      value={movementForm.fecha_caducidad}
                      onChange={(e) => setMovementForm({ ...movementForm, fecha_caducidad: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      data-testid="movimiento-caducidad"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº Orden de Pedido
                      <span className="text-gray-400 ml-1">(Opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={movementForm.numero_pedido}
                      onChange={(e) => setMovementForm({ ...movementForm, numero_pedido: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: PED-2026-001"
                      data-testid="movimiento-pedido"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                    <select
                      value={movementForm.ubicacion}
                      onChange={(e) => setMovementForm({ ...movementForm, ubicacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="almacen">Almacén General</option>
                      <option value="camara_fria">Cámara Fría</option>
                      <option value="congelador">Congelador</option>
                      <option value="bodega">Bodega</option>
                      <option value="despensa">Despensa</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowMovementModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleMovimiento}
                disabled={!movementForm.cantidad || movementForm.cantidad <= 0}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition disabled:opacity-50 ${
                  movementForm.tipo_movimiento === 'entrada' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                data-testid="movimiento-submit"
              >
                {movementForm.tipo_movimiento === 'entrada' ? 'Registrar Entrada' : 'Registrar Salida'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Entrada de Mercancía (Principal) */}
      {showEntradaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 bg-blue-600 text-white rounded-t-xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-lg">📦 Nueva Entrada de Mercancía</h3>
                  <p className="text-sm text-blue-100">Registro con trazabilidad APPCC</p>
                </div>
                <button onClick={() => setShowEntradaModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Ingrediente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ingrediente *
                </label>
                <select
                  value={entradaForm.ingrediente_id}
                  onChange={(e) => setEntradaForm({ ...entradaForm, ingrediente_id: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    formErrors.ingrediente_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="entrada-ingrediente"
                >
                  <option value="">Seleccionar ingrediente...</option>
                  {[...ingredientes].filter(i => i.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre)).map(ing => (
                    <option key={ing.id} value={ing.id}>{ing.nombre} ({ing.unidad})</option>
                  ))}
                </select>
                {formErrors.ingrediente_id && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.ingrediente_id}</p>
                )}
              </div>
              
              {/* Cantidad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad *
                  </label>
                  <input
                    type="number"
                    value={entradaForm.cantidad}
                    onChange={(e) => setEntradaForm({ ...entradaForm, cantidad: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      formErrors.cantidad ? 'border-red-500' : 'border-gray-300'
                    }`}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    data-testid="entrada-cantidad"
                  />
                  {formErrors.cantidad && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.cantidad}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                  <select
                    value={entradaForm.ubicacion}
                    onChange={(e) => setEntradaForm({ ...entradaForm, ubicacion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="entrada-ubicacion"
                  >
                    <option value="almacen">Almacén General</option>
                    <option value="camara_fria">Cámara Fría</option>
                    <option value="congelador">Congelador</option>
                    <option value="bodega">Bodega</option>
                    <option value="despensa">Despensa</option>
                  </select>
                </div>
              </div>
              
              {/* Campos APPCC obligatorios */}
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Datos obligatorios para trazabilidad APPCC
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº Lote Proveedor *
                    </label>
                    <input
                      type="text"
                      value={entradaForm.lote}
                      onChange={(e) => setEntradaForm({ ...entradaForm, lote: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        formErrors.lote ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ej: MET-2025-001234"
                      data-testid="entrada-lote"
                    />
                    {formErrors.lote && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.lote}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Caducidad *
                    </label>
                    <input
                      type="date"
                      value={entradaForm.fecha_caducidad}
                      onChange={(e) => setEntradaForm({ ...entradaForm, fecha_caducidad: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        formErrors.fecha_caducidad ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="entrada-caducidad"
                    />
                    {formErrors.fecha_caducidad && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.fecha_caducidad}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <select
                  value={entradaForm.motivo}
                  onChange={(e) => setEntradaForm({ ...entradaForm, motivo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="entrada-motivo"
                >
                  <option value="compra">Compra a proveedor</option>
                  <option value="devolucion">Devolución de evento</option>
                  <option value="ajuste_inventario">Ajuste de inventario</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowEntradaModal(false);
                  setFormErrors({});
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleEntradaMercancia}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                data-testid="entrada-submit"
              >
                Registrar Entrada
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Movimiento */}
      {showEditModal && editingMovimiento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <Edit2 className="w-5 h-5" />
                  <h3 className="font-semibold">Editar Movimiento</h3>
                </div>
                <button onClick={() => setShowEditModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingMovimiento.cantidad}
                  onChange={(e) => setEditingMovimiento({ ...editingMovimiento, cantidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <select
                  value={editingMovimiento.motivo}
                  onChange={(e) => setEditingMovimiento({ ...editingMovimiento, motivo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="compra">Compra</option>
                  <option value="consumo">Consumo</option>
                  <option value="uso_evento">Uso en evento</option>
                  <option value="merma">Merma</option>
                  <option value="caducidad">Caducidad</option>
                  <option value="devolucion">Devolución</option>
                  <option value="ajuste_inventario">Ajuste de inventario</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nº Orden de Pedido</label>
                <input
                  type="text"
                  value={editingMovimiento.numero_pedido || ''}
                  onChange={(e) => setEditingMovimiento({ ...editingMovimiento, numero_pedido: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: PED-2026-001"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lote afectado <span className="text-red-500">*</span>
                </label>
                {lotesDisponibles.length > 0 ? (
                  <select
                    value={editingMovimiento.lote_id || ''}
                    onChange={(e) => {
                      const selectedLote = lotesDisponibles.find(l => l.id === e.target.value);
                      setEditingMovimiento({ 
                        ...editingMovimiento, 
                        lote_id: e.target.value,
                        lote: selectedLote?.lote_proveedor || selectedLote?.id || ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Seleccionar lote --</option>
                    {lotesDisponibles.map(lote => (
                      <option key={lote.id} value={lote.id}>
                        {lote.lote_proveedor || lote.id?.substring(0,8)} - 
                        Stock: {(lote.cantidad_actual || 0).toFixed(2)} - 
                        Cad: {lote.fecha_caducidad ? new Date(lote.fecha_caducidad).toLocaleDateString('es-ES') : 'Sin fecha'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
                    No hay lotes disponibles para este ingrediente
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona el lote al que afecta este movimiento
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveMovimiento}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Lote */}
      {showEditLoteModal && editingLote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Editar Lote</h3>
              <button onClick={() => setShowEditLoteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nº Lote Proveedor</label>
                <input
                  type="text"
                  value={editingLote.lote_proveedor || ''}
                  onChange={(e) => setEditingLote({ ...editingLote, lote_proveedor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Número de lote del proveedor"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad Actual</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingLote.cantidad_actual}
                  onChange={(e) => setEditingLote({ ...editingLote, cantidad_actual: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Cantidad"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Caducidad</label>
                <input
                  type="date"
                  value={editingLote.fecha_caducidad || ''}
                  onChange={(e) => setEditingLote({ ...editingLote, fecha_caducidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                <select
                  value={editingLote.ubicacion || 'almacen'}
                  onChange={(e) => setEditingLote({ ...editingLote, ubicacion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="almacen">Almacén</option>
                  <option value="camara_frio">Cámara de Frío</option>
                  <option value="congelador">Congelador</option>
                  <option value="despensa">Despensa</option>
                </select>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowEditLoteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveLote}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {esDireccion && (
        <DiagnosticoLibroStock
          abierto={showDiagnosticoLibro}
          onCerrar={() => setShowDiagnosticoLibro(false)}
          onCambio={fetchData}
        />
      )}
    </div>
  );
};

export default StockLotes;
